import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Task } from '@amzn/awscat-pattern-library-service-model';
import { TiptapOutputType } from '@amzn/awscat-editor';
import _ from 'lodash';

import { deepClone } from '../../api/UtilsApi';
import { useContentEditorService } from '../../contexts/ContentEditorServiceContext';
import { usePendingFlushCoordinator } from '../../contexts/PendingFlushCoordinatorContext';
import { useContent } from '../v2-hooks/useContent';
import { tryUpdateEpic } from '../../services/EpicOperations';
import { tryDeleteTask } from '../../services/TaskOperations';

export interface PatternTaskViewModel {
  readonly isEditing: boolean;
  readonly startEditing: () => void;
  readonly saveEditing: () => void;
  readonly cancelEditing: () => void;
  readonly deleteTask: () => void;
  /** Stages a title or description edit (debounced 2s). */
  readonly stageFieldEdit: (field: 'title' | 'description', value: TiptapOutputType) => void;
  /** Stages a skills string edit (immediate — Multiselect fires on commit). */
  readonly stageSkillsEdit: (skills: string) => void;
}

/** Per-task-row ViewModel managing inline edit lifecycle for one task. */
export function usePatternTaskViewModel(
  epicId: string,
  task: Task,
  initiallyEditing: boolean,
): PatternTaskViewModel | undefined {
  const service = useContentEditorService();
  const flushCoordinator = usePendingFlushCoordinator();
  const content = useContent();

  const [isEditing, setIsEditing] = useState(initiallyEditing);
  const preEditTaskRef = useRef<Task | undefined>(undefined);

  const startEditing = useCallback(() => {
    preEditTaskRef.current = deepClone(task);
    setIsEditing(true);
  }, [task]);

  const deleteTask = useCallback(() => {
    const liveContent = service.content;
    if (!liveContent) return;
    const updated = tryDeleteTask(liveContent, epicId, task.id);
    if (updated) service.stageEdit(updated);
  }, [service, epicId, task.id]);

  // Pending Tiptap field edit — debounced to avoid expensive tree
  // traversal on every keystroke.
  const pendingFieldRef = useRef<
    { field: 'title' | 'description'; value: TiptapOutputType } | undefined
  >(undefined);

  const flushFieldEdit = useCallback(() => {
    const pending = pendingFieldRef.current;
    if (!pending) return;
    pendingFieldRef.current = undefined;

    // Use service.content (live source of truth) instead of the stale
    // ref — prevents reviving a deleted epic on flush-during-unmount.
    const liveContent = service.content;
    if (!liveContent) return;

    const updated = tryUpdateEpic(liveContent, epicId, (epic) => ({
      ...epic,
      tasks: epic.tasks.map((t) =>
        t.id === task.id ? { ...t, [pending.field]: pending.value } : t,
      ),
    }));
    if (updated) service.stageEdit(updated);
  }, [service, epicId, task.id]);

  const debouncedFlush = useMemo(() => _.debounce(flushFieldEdit, 2000), [flushFieldEdit]);

  // Flush on unmount so in-flight keystrokes aren't dropped.
  useEffect(
    () => () => {
      debouncedFlush.flush();
    },
    [debouncedFlush],
  );

  // While mounted, let a browser-close drain stage the pending keystroke too.
  useEffect(() => {
    return flushCoordinator.registerFlusher(() => debouncedFlush.flush());
  }, [flushCoordinator, debouncedFlush]);

  const saveEditing = useCallback(() => {
    debouncedFlush.flush();
    preEditTaskRef.current = undefined;
    setIsEditing(false);
  }, [debouncedFlush]);

  // When Cancel is clicked, restore only this task to its pre-edit state.
  const cancelEditing = useCallback(() => {
    debouncedFlush.cancel();
    pendingFieldRef.current = undefined;

    if (preEditTaskRef.current) {
      const snapshot = preEditTaskRef.current;
      const liveContent = service.content;
      if (liveContent) {
        const updated = tryUpdateEpic(liveContent, epicId, (epic) => ({
          ...epic,
          tasks: epic.tasks.map((t) => (t.id === task.id ? snapshot : t)),
        }));
        if (updated) service.stageEdit(updated);
      }
    }

    preEditTaskRef.current = undefined;
    setIsEditing(false);
  }, [service, epicId, task.id, debouncedFlush]);

  const stageFieldEdit = useCallback(
    (field: 'title' | 'description', value: TiptapOutputType) => {
      if (pendingFieldRef.current && pendingFieldRef.current.field !== field) {
        debouncedFlush.flush();
      }
      pendingFieldRef.current = { field, value };
      debouncedFlush();
    },
    [debouncedFlush],
  );

  // Skills don't need debounce — Multiselect fires on user commit.
  const stageSkillsEdit = useCallback(
    (skills: string) => {
      const liveContent = service.content;
      if (!liveContent) return;
      const updated = tryUpdateEpic(liveContent, epicId, (epic) => ({
        ...epic,
        tasks: epic.tasks.map((t) => (t.id === task.id ? { ...t, skills } : t)),
      }));
      if (updated) service.stageEdit(updated);
    },
    [service, epicId, task.id],
  );

  // A parent-teardown render can transiently clear content before this view
  // unmounts; return undefined so the view renders null instead of throwing.
  if (!content) return undefined;

  return {
    isEditing,
    startEditing,
    saveEditing,
    cancelEditing,
    deleteTask,
    stageFieldEdit,
    stageSkillsEdit,
  };
}
